import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { faChartDiagram } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNetworkWired, faWindowRestore } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import Pagination from '../components/common/Pagination';
import { getPlannersConfig, createPlanner, updatePlanner, getPlannersCount } from '../services/plannerService';
import toastService from '../services/toastService';
import { getModels } from '../services/modelHubService';
import { getKnowledgeBases } from '../services/knowledgeBaseService_v2';
import { MultiSelectDropdownSearch } from '../components/common/MultiSelectSearch';
import { fetchMCPServer } from '../services/MCPService';
import { getAgents } from '../services/agentService_V2';
import { getPipelines } from '../services/pipelineService';
import { getEntitiesByPagination } from '../services/entityService';
import CoPilotChat from '../components/email/CoPilotChat';
import { v4 as uuid } from 'uuid';
import EmptyStatePage from '../components/common/EmptyStatePage';
import { Loader, LoaderContainer } from '../components/common/Loader';
import PromptPreview from '../components/common/PromptPreview';
import { useToast } from '../components/common/ToastProvider';
import GuardrailsEditor from '../components/guardrails/GuardrailsEditor';
import { getTools } from '../services/toolService';

const Container = styled.div`
  padding: 2rem;
  height: 100%;
  background: #ffffff;
  color: #333333;
  position: relative;
  overflow-x: hidden;
`;

const SideChatPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: ${props => props.width || 420}px;
  max-width: 100vw;
  min-width: 320px;
  z-index: 1000;
  background: #fff;
  display: flex;
  flex-direction: column;
  transition: width 0.2s cubic-bezier(.4,0,.2,1);
  overflow: hidden;
  border-radius: 0;
  box-shadow: none;
  margin: 0;
`;

const ResizeHandle = styled.div`
  position: absolute;
  left: -8px;
  top: 0;
  bottom: 0;
  width: 16px;
  cursor: ew-resize;
  z-index: 1010;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  &:hover {
    background: rgba(33,150,243,0.08);
  }
`;

const CloseChatButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  z-index: 1010;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  span {
    margin-left: 0.5rem;
    font-size: 0.9rem;
    background: rgba(0, 102, 255, 0.1);
    color: var(--primary);
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
  }
`;

const Card = styled(motion.div)`
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 102, 255, 0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 102, 255, 0.1);
    border-color: rgba(0, 102, 255, 0.3);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const PlannerCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const PlannerHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const PlannerIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.5rem;
  color: var(--primary);
  // font-family: 'Orbitron', sans-serif;
`;

const PlannerInfo = styled.div`
  flex: 1;
`;

const PlannerName = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
`;

const PlannerType = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const PlannerDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  flex: 1;
`;

const PlannerStatus = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${props => props.active ? '#10B981' : '#FF8383'};
  margin-bottom: 1rem;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.active ? '#10B981' : '#FF8383'};
  }
`;

const Button = styled(motion.button)`
  padding: 0.8rem 1.5rem;
  background: ${props => props.primary ? 'var(--gradient-primary)' : '#ffffff'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.primary ? 'var(--gradient-primary)' : 'rgba(0, 102, 255, 0.03)'};
    border-color: ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.2)'};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const PlannerButton = styled(motion.button)`
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;
  width: 100%;
  background: ${props => props.primary ? 'var(--gradient-primary)' : '#ffffff'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  
  &:hover {
    background: ${props => props.primary ? 'var(--gradient-primary)' : 'rgba(0, 102, 255, 0.03)'};
    border-color: ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.2)'};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SearchBar = styled.div`
  display: flex;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.8rem 1rem;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin: 2rem 0;
`;

// Modal & multi-step setup styled components (simplified from AgentStudio_V2)
const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)`
  width: 900px;
  max-width: 90%;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  background: var(--gradient-primary);
  // font-family: 'Manrope', sans-serif;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  
  &:hover {
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  min-height: 120px;
  resize: vertical;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 18px;
    left: 0;
    width: 100%;
    height: 2px;
    background: rgba(0, 102, 255, 0.1);
    z-index: 0;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20%;
  cursor: pointer;
  z-index: 1;
`;

const StepCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.active ? 'var(--gradient-primary)' : props.completed ? 'var(--primary)' : 'rgba(0, 102, 255, 0.1)'};
  color: ${props => props.active || props.completed ? '#ffffff' : 'var(--text-secondary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StepLabel = styled.div`
  font-size: 0.8rem;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-weight: ${props => props.active ? '600' : '400'};
  text-align: center;
`;

const InfoBox = styled.div`
  background: rgba(0, 102, 255, 0.05);
  border-left: 3px solid var(--primary);
  padding: 1rem;
  border-radius: 0 8px 8px 0;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 102, 255, 0.1);
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 102, 255, 0.1);
`;

const ModalTab = styled.button`
  padding: 0.8rem 1.2rem;
  background: transparent;
  border: none;
  font-size: 0.9rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.active ? 'var(--primary)' : 'transparent'};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SelectableCard = styled.div`
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.selected ? 'var(--primary)' : 'rgba(0, 102, 255, 0.1)'};
  background: ${props => props.selected ? 'rgba(0, 102, 255, 0.05)' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-2px);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: ${props => props.selected ? 'var(--primary)' : 'var(--text-secondary)'};
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  color: ${props => props.selected ? 'var(--primary)' : 'var(--text-primary)'};
`;

const CardDescription = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const ToggleSwitch = styled.div`
  width: 40px;
  height: 20px;
  background: ${props => props.on ? 'var(--primary)' : 'rgba(0, 102, 255, 0.1)'};
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background: #ffffff;
    border-radius: 50%;
    top: 2px;
    left: ${props => props.on ? 'calc(100% - 18px)' : '2px'};
    transition: all 0.3s ease;
  }
`;

const Slider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: rgba(0, 102, 255, 0.1);
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--primary);
    cursor: pointer;
  }
`;

const CodeEditor = styled.div`
  font-family: 'Roboto Mono', monospace;
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 1rem;
  border-radius: 8px;
  min-height: 200px;
  white-space: pre-wrap;
`;

const INTERNAL_PIPELINE_TOOL_TYPE = 'internal_pipeline';

const DEFAULT_BUILTIN_TOOLS = [
  {
    type: 'builtin',
    sub_type: 'calculator',
    code: 'calculator',
    name: 'Calculator Tool',
    description:
      'A safe calculator tool for evaluating arithmetic expressions.',
    instructions:
      'Use this tool when the user asks for arithmetic or numerical calculation.',
  },
];




const Planner = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debounceSearchQuery, setDebounceSearchQuery] = useState(searchQuery);
  const [plannerList, setPlannerList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [selectedPlanner, setSelectedPlanner] = useState(null);
  const [currentSetupStep, setCurrentSetupStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState([]);
  const [modalTab, setModalTab] = useState('general');
  const [modalTabPrompt, setModalTabPrompt] = useState('system');
  const [modalTabSecurity, setModalTabSecurity] = useState('security');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [modelList, setModelList] = useState([]);
  const [kbList, setKbList] = useState([]);
  const [mcpToolsData, setMcpToolsData] = useState(null);
  const [selectedMCPServer, setSelectedMCPServer] = useState(null);
  const [systemPromptDraft, setSystemPromptDraft] = useState('');
  const [showPromptTemplates, setShowPromptTemplates] = useState(false);
  const [exampleConversations, setExampleConversations] = useState([]);
  const [showExampleEditor, setShowExampleEditor] = useState(false);
  const [editingExampleIndex, setEditingExampleIndex] = useState(-1);
  const [currentExample, setCurrentExample] = useState({ title: '', conversations: [] });
  const [deploymentEnvironment, setDeploymentEnvironment] = useState('');
  const [deploymentType, setDeploymentType] = useState('immediate');
  const [deploymentSchedule, setDeploymentSchedule] = useState('');
  const [systemPromptMode, setSystemPromptMode] = useState('editor');
  const [agentList, setAgentList] = useState([]);
  const [plannerOptions, setPlannerOptions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [openPlannerCopilot, setOpenPlannerCopilot] = useState(false);
  const [currentPlannerToRun, setCurrentPlannerToRun] = useState(null);
  const [chatPanelWidth, setChatPanelWidth] = useState(420);
  const [isLoading, setIsLoading] = useState(true);
  const [toolPipelines, setToolPipelines] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityOptionsLoading, setEntityOptionsLoading] = useState(false);
  const draggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(420);
  const builtinTools = DEFAULT_BUILTIN_TOOLS;
  const { showToast } = useToast();
  const [externalMcpTools, setExternalMcpTools] = useState([]);
  const [externalMcpToolsLoading, setExternalMcpToolsLoading] = useState(false);


  const initialPlanner = {
    id: 0,
    code: 'PL-' + new Date().getTime(),
    name: '',
    reasoning: false,
    status: 'ACTIVE',
    type: '',
    business_domain: '',
    icon: '',
    description: '',
    member_code_list: [],
    general_settings: {
      model_code: '',
      model_temperature: 0.7,
      max_tokens: 2000,
    },
    advanced_settings: {
      hosted_on: '',
      caching_strategy: 'dynamic',
      top_p: 0.9,
      frequency_penalty: 0.5,
      presence_penalty: 0,
      response_format: 'auto',
    },
    knowledge_base: {
      knowledgebase_code: '',
      vector_database: 'pinecone',
      embedding_model: 'openai',
    },
    prompt: {
      system_prompt: '',
    },
    security: {
      security_settings: {
        data_encryption: false,
        pii_handling: false,
      },
      ai_guardrails: [],
    },
    deployment: {
      deployment_environment_option: '',
      deployment_schedule: '',
    },
    tool_settings_list: [],
    disabled_builtin_tools: [],
    mcp_servers: {},
  };

  useEffect(() => {
    const fetchPlannerData = async () => {
      let filter = {};
      if(debounceSearchQuery) {
        filter = { 'name' :{ 'ilike' : `${debounceSearchQuery}`} }
      }
      const sort = {};
      const query = { skip: (currentPage - 1) * itemsPerPage, limit: itemsPerPage };
      try {
        setIsLoading(true);
        const response = await getPlannersConfig(filter, sort, query);
        const list = Array.isArray(response) ? response : response?.data || [];
        setPlannerList(list || []);
      } catch (error) {
        console.error('Error fetching planners:', error);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlannerData();
  }, [currentPage, debounceSearchQuery]);

  useEffect(() => {
    const fetchPlannerCount = async () => {
      try {
        let filter = {};
        if(debounceSearchQuery) {
          filter = {...filter,'name' : { 'ilike' : `${debounceSearchQuery}`} }
        }
        const count = await getPlannersCount(filter);
        setTotalItems(count || 0);
      } catch (error) {
        console.error('Error fetching planners count:', error);
      }
    };

    fetchPlannerCount();
  }, [debounceSearchQuery]);

    useEffect(() => {
      const timer = setTimeout(() => { setDebounceSearchQuery(searchQuery) }, 300);
      return () => {
        clearTimeout(timer)
      }
    }, [searchQuery])

  const fetchPlannersList = async () => {
    try {
      const resp = await getPlannersConfig({}, {}, {
        skip: 0,
        limit: 100
      });
      setPlannerOptions(resp || []);
    } catch (error) {
      console.error('Error fetching planners:', error);
    }
  };

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await getModels();
        setModelList(resp || []);
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };

    const fetchPlannerCount = async () => {
      try {
        const resp = await getPlannersCount();
        setTotalItems(resp);
      } catch (error) {
        console.log("Error in fetching the planner count", error)
      }
    }

    const fetchKnowledgeBasesList = async () => {
      try {
        const resp = await getKnowledgeBases();
        setKbList(resp || []);
      } catch (error) {
        console.error('Error fetching knowledge bases:', error);
      }
    };

    const fetchMcpServers = async () => {
      try {
        const data = await fetchMCPServer();
        setMcpToolsData(data);
        const serverList = data?.server_indexed_mcp_tools && Object.keys(data.server_indexed_mcp_tools);
        if (serverList && serverList.length > 0) {
          setSelectedMCPServer(serverList[0]);
        }
      } catch (error) {
        console.error('Error fetching MCP servers:', error);
      }
    };

    const loadToolPipelines = async () => {
      try {
        setToolsLoading(true);
        const queryParams = 'skip=0&limit=100';
        const resp = await getPipelines({ type: 'tool' }, {}, queryParams);
        const list = Array.isArray(resp) ? resp : [];
        setToolPipelines(list);
      } catch (error) {
        console.error('Error while fetching tool pipelines', error);
      } finally {
        setToolsLoading(false);
      }
    };

    const loadBaseEntities = async () => {
      try {
        setEntityOptionsLoading(true);
        const resp = await getEntitiesByPagination({ type: 'entity' }, {}, { skip: 0, limit: 1000 });
        const list = Array.isArray(resp) ? resp : [];
        setEntityOptions(list.map(e => ({ name: e.name || e.code, code: e.code })));
      } catch (error) {
        setEntityOptions([]);
      } finally {
        setEntityOptionsLoading(false);
      }
    };
    
    const loadExternalMcpTools = async () => {
      try {
        setExternalMcpToolsLoading(true);

        const response = await getTools(
          {
            tool_type: 'mcp_external',
          },
          {
            name: 'asc',
          },
          'skip=0&limit=100'
        );

        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        setExternalMcpTools(list);
      } catch (error) {
        console.error(
          'Error while fetching External MCP tools:',
          error
        );

        setExternalMcpTools([]);
      } finally {
        setExternalMcpToolsLoading(false);
      }
    };

    const fetchAgentsList = async () => {
      try {
        const queryParams = `skip=0&limit=100`;
        const resp = await getAgents({}, {}, queryParams);
        setAgentList(resp || []);
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
    };

    fetchModels();
    fetchPlannerCount();
    fetchKnowledgeBasesList();
    fetchMcpServers();
    fetchAgentsList();
    fetchPlannersList();
    loadToolPipelines();
    loadExternalMcpTools();
    loadBaseEntities();
  }, []);

  const handleRunPlanner = (plannerCode) => {
    setCurrentPlannerToRun(plannerCode);
    setOpenPlannerCopilot(true);
  };

  const onResizeStart = (clientX) => {
    draggingRef.current = true;
    startXRef.current = clientX;
    startWidthRef.current = chatPanelWidth;
    document.addEventListener('mousemove', mouseMove);
    document.addEventListener('mouseup', mouseUp);
    document.addEventListener('touchmove', touchMove, { passive: false });
    document.addEventListener('touchend', touchEnd);
  };

  const mouseMove = (e) => {
    if (!draggingRef.current) return;
    const delta = e.clientX - startXRef.current;
    let newW = startWidthRef.current - delta;
    const maxW = Math.min(window.innerWidth, 700);
    newW = Math.max(320, Math.min(newW, maxW));
    setChatPanelWidth(newW);
  };

  const mouseUp = () => {
    draggingRef.current = false;
    document.removeEventListener('mousemove', mouseMove);
    document.removeEventListener('mouseup', mouseUp);
    document.removeEventListener('touchmove', touchMove);
    document.removeEventListener('touchend', touchEnd);
  };

  const touchMove = (e) => {
    if (!draggingRef.current) return;
    const clientX = e.touches[0].clientX;
    const delta = clientX - startXRef.current;
    let newW = startWidthRef.current - delta;
    const maxW = Math.min(window.innerWidth, 700);
    newW = Math.max(320, Math.min(newW, maxW));
    setChatPanelWidth(newW);
    e.preventDefault();
  };

  const touchEnd = () => mouseUp();

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', mouseUp);
      document.removeEventListener('touchmove', touchMove);
      document.removeEventListener('touchend', touchEnd);
    };
  }, []);

  const handleViewPlanner = (plannerId) => {
    navigate(`/planner-details/${plannerId}`);
  };

const DEFAULT_ICON = '/assets/images/agents/clipBoard.png';

const getSafeIcon = (icon) => {
  if (typeof icon !== 'string') {
    return DEFAULT_ICON;
  }
  const iconsPaths = []

  if (
    icon.startsWith('/assets/images/agents/') &&
    iconsPaths.includes(icon)
  ) {
    return icon;
  }

  return DEFAULT_ICON;
};

  const openPlannerModal = (planner = null) => {
    const base = { ...initialPlanner,tool_settings_list: [],disabled_builtin_tools: [], };
    const merged = planner ? { ...base, ...planner,tool_settings_list: (planner.tool_settings_list || []).filter(
          (tool) => tool.type !== 'builtin'),disabled_builtin_tools: planner.disabled_builtin_tools || [], } : base;
    setSelectedPlanner(merged);
    setShowModal(true);
    setCurrentSetupStep(1);
    setValidationErrors([]);
    setModalTab('general');
    setModalTabPrompt('system');
    setSystemPromptDraft(merged.prompt?.system_prompt || '');
    setExampleConversations(merged.prompt?.example_conversation || []);
    setSystemPromptMode('editor');

    // Pre-select member agents and planners based on member_code_list when editing
    const preselectedAgents = agentList
      .filter((agent) => (merged.member_code_list || []).includes(agent.code))
      .map((agent) => ({
        name: `${agent.name || agent.code} (Agent)`,
        desc: agent.description,
        code: agent.code,
      }));
    
    const preselectedPlanners = plannerOptions
      .filter((planner) => (merged.member_code_list || []).includes(planner.code))
      .map((planner) => ({
        name: `${planner.name || planner.code} (Planner)`,
        desc: planner.description,
        code: planner.code,
      }));
    
    const preselectedMembers = [...preselectedAgents, ...preselectedPlanners];
    setSelectedMembers(preselectedMembers);
  };

  const closePlannerModal = () => {
    setShowModal(false);
    setSelectedPlanner(null);
    setValidationErrors([]);
    setSelectedMembers([]);
  };

  const nextStep = () => {
    setCurrentSetupStep((prev) => Math.min(prev + 1, 5));
  };

  const prevStep = () => {
    setCurrentSetupStep((prev) => Math.max(prev - 1, 1));
  };

  const handleFieldChange = (field, value) => {
    setSelectedPlanner((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddToolSetting = () => {
    setSelectedPlanner((prev) => ({
      ...prev,
      tool_settings_list: [
        ...(prev?.tool_settings_list || []),
        { type: 'mcp_internal', sub_type: 'pipeline', code: '' },
      ],
    }));
  };



  const handleUpdateToolSetting = (index, field, value) => {
    setSelectedPlanner((prev) => {
      const list = [...(prev?.tool_settings_list || [])];

      const existing = list[index] || {
        type: 'mcp_internal',
        sub_type: 'pipeline',
        code: '',
        instructions: '',
      };

      list[index] = {
        ...existing,
        [field]: value,
      };

      if (field === 'type' && value === 'mcp_internal') {
        list[index] = {
          type: 'mcp_internal',
          sub_type: 'pipeline',
          code: '',
          instructions: existing.instructions || '',
        };
      }

      if (
        field === 'type' &&
        value === INTERNAL_PIPELINE_TOOL_TYPE
      ) {
        list[index] = {
          type: INTERNAL_PIPELINE_TOOL_TYPE,
          sub_type: 'pipeline',
          code: '',
          instructions: existing.instructions || '',
        };
      }

      if (field === 'type' && value === 'mcp_external') {
        list[index] = {
          type: 'mcp_external',
          sub_type: 'mcp',
          code: '',
          instructions: existing.instructions || '',
        };
      }

      if (field === 'type' && value === 'entity') {
        list[index] = {
          type: 'entity',
          sub_type: 'entity',
          code: 'entity_data_tool',
          entity_code: '',
          operations:
            Array.isArray(existing.operations) &&
            existing.operations.length > 0
              ? existing.operations
              : ['fetch'],
          read_only:
            typeof existing.read_only === 'boolean'
              ? existing.read_only
              : true,
          default_limit:
            typeof existing.default_limit === 'number'
              ? existing.default_limit
              : 200,
          instructions: existing.instructions || '',
        };
      }

      return {
        ...prev,
        tool_settings_list: list,
      };
    });
  };



  const normalizeToolCode = (code) =>
    String(code || '').trim().toLowerCase();


  
  const normalizedExternalMcpTools = (
    externalMcpTools || []
  )
    .filter(
      (tool) =>
        typeof tool?.code === 'string' &&
        tool.code.trim()
    )
    .map((tool) => {
      const code = tool.code.trim();

      return {
        ...tool,
        code,
        name:
          tool.name ||
          tool.displayName ||
          tool.display_name ||
          code,
        description: tool.description || '',
      };
    });

  const isExternalMcpAlreadySelected = (toolCode, currentIndex) => {
    const normalizedCode = normalizeToolCode(toolCode);

    if (!normalizedCode) {
      return false;
    }

    return (selectedPlanner?.tool_settings_list || []).some(
      (setting, index) =>
        index !== currentIndex &&
        setting.type === 'mcp_external' &&
        normalizeToolCode(setting.code) === normalizedCode
    );
  };




  const isBuiltinToolEnabled = (toolCode) => {
    const normalizedToolCode = normalizeToolCode(toolCode);

    const disabledBuiltinTools = (
      selectedPlanner?.disabled_builtin_tools || []
    ).map(normalizeToolCode);

    return !disabledBuiltinTools.includes(normalizedToolCode);
  };

  const handleToggleBuiltinTool = (toolCode, enabled) => {
    setSelectedPlanner((prev) => {
      const normalizedToolCode = normalizeToolCode(toolCode);

      const disabledBuiltinTools =
        prev?.disabled_builtin_tools || [];

      return {
        ...prev,
        disabled_builtin_tools: enabled
          ? disabledBuiltinTools.filter(
              (code) =>
                normalizeToolCode(code) !== normalizedToolCode
            )
          : disabledBuiltinTools.some(
                (code) =>
                  normalizeToolCode(code) === normalizedToolCode
              )
            ? disabledBuiltinTools
            : [...disabledBuiltinTools, toolCode],
      };
    });
  };


  const handleRemoveToolSetting = (index) => {
    setSelectedPlanner((prev) => {
      const list = [...(prev?.tool_settings_list || [])];
      list.splice(index, 1);

      return {
        ...prev,
        tool_settings_list: list,
      };
    });
  };


  const handleNestedFieldChange = (parentField, field, value) => {
    setSelectedPlanner((prev) => ({
      ...prev,
      [parentField]: {
        ...(prev[parentField] || {}),
        [field]: value,
      },
    }));
  };

  const updateSelectedPlanner = (path, value) => {
    setSelectedPlanner((prev) => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const isLast = i === keys.length - 1;
        if (isLast) {
          current[key] = value;
        } else {
          if (current[key] == null || typeof current[key] !== 'object') {
            current[key] = {};
          }
          current = current[key];
        }
      }
      return updated;
    });
  };

  const validatePlanner = () => {
    const errors = [];
    if (!selectedPlanner) return errors;

    if (!selectedPlanner.code || !selectedPlanner.code.trim()) {
      errors.push('Planner code is required');
    }
    if (!selectedPlanner.name || !selectedPlanner.name.trim()) {
      errors.push('Planner name is required');
    }
    if (!selectedPlanner.type) {
      errors.push('Planner type is required');
    }
    if (!selectedPlanner.business_domain) {
      errors.push('Business domain is required');
    }
    if (!selectedPlanner.description || !selectedPlanner.description.trim()) {
      errors.push('Planner description is required');
    }
    if (!selectedPlanner.general_settings?.model_code) {
      errors.push('Model code is required');
    }
    if (!selectedPlanner.prompt?.system_prompt || selectedPlanner.prompt.system_prompt.trim().length < 20) {
      errors.push('System prompt should be at least 20 characters');
    }

    const invalidExternalMcpTool = (
      selectedPlanner.tool_settings_list || []
    ).some(
      (tool) =>
        tool.type === 'mcp_external' &&
        !tool.code?.trim()
    );

    if (invalidExternalMcpTool) {
      errors.push(
        'Please select an External MCP Tool.'
      );
    }


    return errors;
  };

  const savePlanner = async (planner) => {
    if (planner.id) {

      const resp = await updatePlanner(planner);
      console.log("resp of planere:", resp)
      setPlannerList((prev) =>
        prev.map((p) => (p.id === planner.id ? {...p,...resp} : p))
      );
        showToast({
          message:"Planner updated successfully.",
          type: "success"
        })
      // toastService.success('Planner updated successfully');
    } else {

      const { id, ...plannerWithoutId } = planner;
      const resp = await createPlanner(plannerWithoutId);
      setPlannerList((prev) => [...prev, resp]);
        showToast({
          message:"Planner created successfully.",
          type: "success"
        })
      // toastService.success('Planner created successfully');
    }
  };



  const handleSubmitPlanner = async () => {
    const errors = validatePlanner();
    setValidationErrors(errors);

    if (errors.length > 0) {
      return;
    }

    try {
      const cleanedPlanner = {
        ...selectedPlanner,
        tool_settings_list: (
          selectedPlanner.tool_settings_list || []
        ).filter((tool) => tool.type !== 'builtin'),
        disabled_builtin_tools:
          selectedPlanner.disabled_builtin_tools || [],
      };

      await savePlanner(cleanedPlanner);
      await fetchPlannersList();
      closePlannerModal();
    } catch (error) {
      console.error('Failed to save planner:', error);
      toastService.error('Failed to save planner');
    }
  };

  const toggleStatusOfThePlanner = async (planner) => {
      const updatedPlanner = {
        ...planner,
        status: planner.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
      };

      setPlannerList((prev) =>
        prev.map((pl) =>
          pl.id === planner.id ? updatedPlanner : pl
        )
      );


      const resp = await savePlanner(updatedPlanner);
      if(resp?.id) {
        showToast({
          message:"Planner status updated successfully.",
          type: "success"
        })
      }
    };

  const filteredPlannerList = plannerList.filter(planner => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (planner.name || '').toLowerCase().includes(query) ||
      (planner.type || '').toLowerCase().includes(query) ||
      (planner.business_domain || '').toLowerCase().includes(query)
    );
  });


  const renderPlannerSetupModal = () => {
    if (!selectedPlanner) return null;

    const steps = ['Basic Info', 'Model & Knowledge', 'Prompts', 'Security & Guardrails', 'Review'];

    const memberOptions = [
      ...agentList.map(agent => ({
        name: `${agent.name || agent.code} (Agent)`,
        desc: agent.description,
        code: agent.code,
      })),
      ...plannerOptions
        .filter(planner => planner.code !== selectedPlanner?.code)
        .map(planner => ({
          name: `${planner.name || planner.code} (Planner)`,
          desc: planner.description,
          code: planner.code,
        }))
    ];

    return (
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <ModalHeader>
          <ModalTitle>{selectedPlanner.id ? 'Edit Planner' : 'Design New Planner'}</ModalTitle>
          <CloseButton onClick={closePlannerModal}>×</CloseButton>
        </ModalHeader>

        <StepIndicator>
          {steps.map((step, index) => (
            <Step key={step}>
              <StepCircle
                active={currentSetupStep === index + 1}
                completed={currentSetupStep > index + 1}
                onClick={() => setCurrentSetupStep(index + 1)}
              >
                {currentSetupStep > index + 1 ? '✓' : index + 1}
              </StepCircle>
              <StepLabel active={currentSetupStep === index + 1}>{step}</StepLabel>
            </Step>
          ))}
        </StepIndicator>

        {validationErrors.length > 0 && (
          <div style={{ marginBottom: '1rem', color: '#b91c1c', fontSize: '0.9rem' }}>
            {validationErrors.map((err) => (
              <div key={err}>• {err}</div>
            ))}
          </div>
        )}

        {currentSetupStep === 1 && (
          <>
            <InfoBox>
              Start by defining the basic information about your planner. A clear purpose and scope will help it perform better.
            </InfoBox>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <div style={{ flex: 1 }}>
                <FormGroup>
                  <Label>Planner Code*</Label>
                  <Input
                    type="text"
                    placeholder="Enter planner code"
                    value={selectedPlanner.code || ''}
                    onChange={(e) => handleFieldChange('code', e.target.value)}
                    disabled={!!selectedPlanner.id}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Planner Name*</Label>
                  <Input
                    type="text"
                    placeholder="Enter planner name"
                    value={selectedPlanner.name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Reasoning</Label>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedPlanner.reasoning || false}
                      onChange={(e) => handleFieldChange('reasoning', e.target.checked)}
                      style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Enable reasoning for this planner</span>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Planner Type*</Label>
                  <Select
                    value={selectedPlanner.type || ''}
                    onChange={(e) => handleFieldChange('type', e.target.value)}
                  >
                    <option value="">Select planner type</option>
                    <option value="Insurance">Insurance</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Claims Processing">Claims Processing</option>
                    <option value="Underwriting">Underwriting</option>
                    <option value="Fraud Detection">Fraud Detection</option>
                    <option value="Provider Management">Provider Management</option>
                    <option value="Benefits">Benefits</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Business Domain*</Label>
                  <Select
                    value={selectedPlanner.business_domain || ''}
                    onChange={(e) => handleFieldChange('business_domain', e.target.value)}
                  >
                    <option value="">Select business domain</option>
                    <option value="life_insurance">Life Insurance</option>
                    <option value="health_insurance">Health Insurance</option>
                    <option value="property_insurance">Property Insurance</option>
                    <option value="auto_insurance">Auto Insurance</option>
                    <option value="provider_network">Provider Network</option>
                    <option value="benefits_management">Benefits Management</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Description*</Label>
                  <Textarea
                    placeholder="Describe what this planner is responsible for"
                    value={selectedPlanner.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Members (Agents & Planners)</Label>
                  <MultiSelectDropdownSearch
                    options={memberOptions}
                    selected={selectedMembers}
                    onChange={(newSelected) => {
                      setSelectedMembers(newSelected);
                      setSelectedPlanner((prev) => ({
                        ...prev,
                        member_code_list: newSelected.map((member) => member.code),
                      }));
                    }}
                    placeholder="Select members"
                    searchPlaceholder="Search agents & planners..."
                  />
                </FormGroup>
              </div>
            </div>
          </>
        )}

        {currentSetupStep === 2 && (
          <>
            <InfoBox>
              Configure the AI model and knowledge sources that will power your planner. The right combination will significantly impact performance.
            </InfoBox>

            <TabsContainer>
              <ModalTab
                active={modalTab === 'general'}
                onClick={() => setModalTab('general')}
              >
                General Settings
              </ModalTab>
              <ModalTab
                active={modalTab === 'advanced'}
                onClick={() => setModalTab('advanced')}
              >
                Advanced Settings
              </ModalTab>
              <ModalTab
                active={modalTab === 'knowledge'}
                onClick={() => setModalTab('knowledge')}
              >
                Knowledge Base
              </ModalTab>
              <ModalTab
                active={modalTab === 'MCP Tools'}
                onClick={() => setModalTab('MCP Tools')}
              >
                Tools
              </ModalTab>
            </TabsContainer>

            {modalTab === 'general' && (
              <>
                <FormGroup>
                  <Label>Source</Label>
                  <CardGrid>
                    {modelList.map((model, index) => (
                      <SelectableCard
                        key={index}
                        selected={selectedPlanner.general_settings?.model_code === model.code}
                        onClick={() =>
                          handleNestedFieldChange('general_settings', 'model_code', model.code)
                        }
                      >
                        <CardIcon selected={selectedPlanner.general_settings?.model_code === model.code}>
                          <img
                            src={`assets/images/models/${model.icon}`}
                            alt={model.name}
                            width={70}
                            height={70}
                          />
                        </CardIcon>
                        <CardTitle selected={selectedPlanner.general_settings?.model_code === model.code}>
                          {model.name}
                        </CardTitle>
                        <CardDescription>{model.description}</CardDescription>
                      </SelectableCard>
                    ))}
                  </CardGrid>
                </FormGroup>

                <FormGroup>
                  <Label>Model Parameters</Label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1.5rem',
                      background: 'rgba(0, 102, 255, 0.02)',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 102, 255, 0.1)',
                    }}
                  >
                    <div>
                      <Label
                        style={{
                          fontSize: '0.9rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>Temperature</span>
                        <span style={{ color: 'var(--primary)' }}>
                          {selectedPlanner.general_settings?.model_temperature}
                        </span>
                      </Label>
                      <Slider
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={selectedPlanner.general_settings?.model_temperature ?? 0.7}
                        onChange={(e) =>
                          handleNestedFieldChange(
                            'general_settings',
                            'model_temperature',
                            parseFloat(e.target.value)
                          )
                        }
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem',
                        }}
                      >
                        <span>Precise (0)</span>
                        <span>Creative (1)</span>
                      </div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem',
                        }}
                      >
                        Lower values produce more predictable outputs, higher values more
                        creative ones
                      </div>
                    </div>
                    <div>
                      <Label
                        style={{
                          fontSize: '0.9rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>Max Tokens</span>
                        <span style={{ color: 'var(--primary)' }}>
                          {selectedPlanner.general_settings?.max_tokens}
                        </span>
                      </Label>
                      <Slider
                        type="range"
                        min="1000"
                        max="8000"
                        step="500"
                        value={selectedPlanner.general_settings?.max_tokens ?? 2000}
                        onChange={(e) =>
                          handleNestedFieldChange(
                            'general_settings',
                            'max_tokens',
                            parseFloat(e.target.value)
                          )
                        }
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem',
                        }}
                      >
                        <span>1,000</span>
                        <span>8,000</span>
                      </div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem',
                        }}
                      >
                        Maximum length of the model's response (higher values may increase
                        costs
                      </div>
                    </div>
                  </div>
                </FormGroup>
              </>
            )}

            {modalTab === 'advanced' && (
              <>
                <FormGroup>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <Label style={{ margin: 0 }}>Advanced Model Settings</Label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <span
                        style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}
                      >
                        Show Advanced
                      </span>
                      <ToggleSwitch
                        on={showAdvancedOptions}
                        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      />
                    </div>
                  </div>

                  {showAdvancedOptions && (
                    <div
                      style={{
                        background: 'rgba(0, 102, 255, 0.02)',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(0, 102, 255, 0.1)',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '1rem',
                      }}
                    >
                      <div>
                        <Label
                          style={{
                            fontSize: '0.9rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span>Top P</span>
                          <span style={{ color: 'var(--primary)' }}>
                            {selectedPlanner.advanced_settings?.top_p}
                          </span>
                        </Label>
                        <Slider
                          type="range"
                          min="0.1"
                          max="1"
                          step="0.1"
                          value={selectedPlanner.advanced_settings?.top_p ?? 0.9}
                          onChange={(e) =>
                            handleNestedFieldChange(
                              'advanced_settings',
                              'top_p',
                              parseFloat(e.target.value)
                            )
                          }
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            marginTop: '0.5rem',
                          }}
                        >
                          <span>0.1</span>
                          <span>1</span>
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            marginTop: '0.5rem',
                          }}
                        >
                          Controls diversity via nucleus sampling
                        </div>
                      </div>
                      <div>
                        <Label
                          style={{
                            fontSize: '0.9rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span>Frequency Penalty</span>
                          <span style={{ color: 'var(--primary)' }}>
                            {selectedPlanner.advanced_settings?.frequency_penalty}
                          </span>
                        </Label>
                        <Slider
                          type="range"
                          min="0"
                          max="2"
                          step="0.1"
                          value={
                            selectedPlanner.advanced_settings?.frequency_penalty ?? 0.5
                          }
                          onChange={(e) =>
                            handleNestedFieldChange(
                              'advanced_settings',
                              'frequency_penalty',
                              parseFloat(e.target.value)
                            )
                          }
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            marginTop: '0.5rem',
                          }}
                        >
                          <span>0</span>
                          <span>2</span>
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            marginTop: '0.5rem',
                          }}
                        >
                          Reduces repetition of the same phrases
                        </div>
                      </div>
                      <div>
                        <Label
                          style={{
                            fontSize: '0.9rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span>Presence Penalty</span>
                          <span style={{ color: 'var(--primary)' }}>
                            {selectedPlanner.advanced_settings?.presence_penalty}
                          </span>
                        </Label>
                        <Slider
                          type="range"
                          min="0"
                          max="2"
                          step="0.1"
                          value={
                            selectedPlanner.advanced_settings?.presence_penalty ?? 0
                          }
                          onChange={(e) =>
                            handleNestedFieldChange(
                              'advanced_settings',
                              'presence_penalty',
                              parseFloat(e.target.value)
                            )
                          }
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            marginTop: '0.5rem',
                          }}
                        >
                          <span>0</span>
                          <span>2</span>
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            marginTop: '0.5rem',
                          }}
                        >
                          Encourages talking about new topics
                        </div>
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.9rem' }}>Response Format</Label>
                        <Select
                          value={selectedPlanner.advanced_settings?.response_format || 'auto'}
                          onChange={(e) =>
                            handleNestedFieldChange(
                              'advanced_settings',
                              'response_format',
                              e.target.value
                            )
                          }
                        >
                          <option value="auto">Auto</option>
                          <option value="text">Text</option>
                          <option value="json">JSON</option>
                        </Select>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            marginTop: '0.5rem',
                          }}
                        >
                          Specify the format of model outputs
                        </div>
                      </div>
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Model Hosting</Label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                    }}
                  >
                    <SelectableCard
                      selected={selectedPlanner.advanced_settings?.hosted_on === 'cloud'}
                      onClick={() =>
                        handleNestedFieldChange('advanced_settings', 'hosted_on', 'cloud')
                      }
                    >
                      <CardIcon selected={selectedPlanner.advanced_settings?.hosted_on === 'cloud'}>
                        ☁️
                      </CardIcon>
                      <CardTitle selected={selectedPlanner.advanced_settings?.hosted_on === 'cloud'}>
                        Cloud Hosted
                      </CardTitle>
                      <CardDescription>
                        Use managed cloud infrastructure for easy deployment
                      </CardDescription>
                    </SelectableCard>
                    <SelectableCard
                      selected={selectedPlanner.advanced_settings?.hosted_on === 'onprem'}
                      onClick={() =>
                        handleNestedFieldChange('advanced_settings', 'hosted_on', 'onprem')
                      }
                    >
                      <CardIcon selected={selectedPlanner.advanced_settings?.hosted_on === 'onprem'}>
                        🏢
                      </CardIcon>
                      <CardTitle selected={selectedPlanner.advanced_settings?.hosted_on === 'onprem'}>
                        On-Premises
                      </CardTitle>
                      <CardDescription>
                        Deploy on your own infrastructure for maximum control
                      </CardDescription>
                    </SelectableCard>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Caching Strategy</Label>
                  <Select
                    value={selectedPlanner.advanced_settings?.caching_strategy || 'dynamic'}
                    onChange={(e) =>
                      handleNestedFieldChange(
                        'advanced_settings',
                        'caching_strategy',
                        e.target.value
                      )
                    }
                  >
                    <option value="none">No Caching</option>
                    <option value="static">Static (Cache all responses)</option>
                    <option value="dynamic">Dynamic (Smart caching)</option>
                  </Select>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)',
                      marginTop: '0.5rem',
                    }}
                  >
                    Caching can improve response times and reduce costs for repetitive
                    queries
                  </div>
                </FormGroup>
              </>
            )}

            {modalTab === 'knowledge' && (
              <>
                <FormGroup>
                  <Label>Knowledge Base</Label>
                  <div style={{ marginBottom: '1rem' }}>
                    <Select
                      value={selectedPlanner.knowledge_base?.knowledgebase_code || ''}
                      onChange={(e) =>
                        handleNestedFieldChange(
                          'knowledge_base',
                          'knowledgebase_code',
                          e.target.value
                        )
                      }
                    >
                      <option value="">Add knowledge base</option>
                      {kbList.map((kb, index) => (
                        <option key={index} value={kb.code}>
                          {kb.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Vector Database</Label>
                  <Select
                    value={selectedPlanner.knowledge_base?.vector_database || 'pinecone'}
                    onChange={(e) =>
                      handleNestedFieldChange(
                        'knowledge_base',
                        'vector_database',
                        e.target.value
                      )
                    }
                  >
                    <option value="pinecone">Pinecone</option>
                    <option value="qdrant">Qdrant</option>
                    <option value="weaviate">Weaviate</option>
                    <option value="redis">Redis</option>
                    <option value="custom">Custom</option>
                  </Select>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)',
                      marginTop: '0.5rem',
                    }}
                  >
                    Vector database used for semantic search and retrieval
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Embedding Model</Label>
                  <Select
                    value={selectedPlanner.knowledge_base?.embedding_model || 'openai'}
                    onChange={(e) =>
                      handleNestedFieldChange(
                        'knowledge_base',
                        'embedding_model',
                        e.target.value
                      )
                    }
                  >
                    <option value="openai">OpenAI Embeddings</option>
                    <option value="cohere">Cohere Embeddings</option>
                    <option value="sentence-transformers">
                      Sentence Transformers
                    </option>
                    <option value="custom">Custom</option>
                  </Select>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)',
                      marginTop: '0.5rem',
                    }}
                  >
                    Model used to convert text into vector embeddings
                  </div>
                </FormGroup>
              </>
            )}

            {modalTab === 'MCP Tools' && (
              <>
                {(() => {
                  const toolSettings = selectedPlanner?.tool_settings_list || [];
                  const hasToolPipelines = Array.isArray(toolPipelines) && toolPipelines.length > 0;
                  const normalizedToolPipelines = (toolPipelines || []).map((p) => ({
                    ...p,
                    name: p.name || p.code,
                  }));

                  return (
                    <>
                      <FormGroup>
                        <Label>Tools</Label>

                        <div
                          style={{
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 102, 255, 0.12)',
                            background: '#fff',
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              marginBottom: '0.75rem',
                            }}
                          >
                            Built-in Tools
                          </div>

                          {builtinTools.map((tool) => {
                            const enabled = isBuiltinToolEnabled(tool.code);

                            return (
                              <div
                                key={tool.code}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '1rem',
                                  padding: '0.85rem',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(0, 102, 255, 0.1)',
                                  background: 'rgba(0, 102, 255, 0.02)',
                                }}
                              >
                                <ToggleSwitch
                                  role="switch"
                                  aria-checked={enabled}
                                  on={enabled}
                                  onClick={() =>
                                    handleToggleBuiltinTool(tool.code, !enabled)
                                  }
                                />

                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      gap: '1rem',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                      }}
                                    >
                                      {tool.name || tool.code}
                                    </div>

                                    <span
                                      style={{
                                        fontSize: '0.75rem',
                                        padding: '0.15rem 0.5rem',
                                        borderRadius: '999px',
                                        background: 'rgba(0, 102, 255, 0.08)',
                                        color: 'var(--primary)',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Builtin
                                    </span>
                                  </div>

                                  {tool.description && (
                                    <div
                                      style={{
                                        fontSize: '0.8rem',
                                        color: 'var(--text-secondary)',
                                        marginTop: '0.25rem',
                                      }}
                                    >
                                      {tool.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {toolsLoading && (
                          <div
                            style={{
                              fontSize: '0.9rem',
                              color: 'var(--text-secondary)',
                              marginBottom: '1rem',
                            }}
                          >
                            Loading Internal Tools...
                          </div>
                        )}

                        {!toolsLoading && !hasToolPipelines && (
                          <div
                            style={{
                              fontSize: '0.9rem',
                              color: 'var(--text-secondary)',
                              marginBottom: '1rem',
                            }}
                          >
                            No Internal Tools found. Create a pipeline of type{' '}
                            <strong>tool</strong> in Agent Pipelines first.
                          </div>
                        )}

                        {toolSettings.map((tool, index) => (
                          <div
                            key={index}
                            style={{
                              marginBottom: '1rem',
                              padding: '1rem',
                              borderRadius: '8px',
                              border: '1px solid rgba(0, 102, 255, 0.1)',
                              background: 'rgba(0, 102, 255, 0.02)',
                            }}
                          >
                            <div>
                              <div style={{ marginBottom: '0.75rem' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.5rem' }}>Select Tool Type</div>
                                <CardGrid style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                                  <SelectableCard
                                    role="button"
                                    aria-pressed={(tool.type || 'mcp_internal') === 'mcp_internal'}
                                    selected={(tool.type || 'mcp_internal') === 'mcp_internal'}
                                    onClick={() => handleUpdateToolSetting(index, 'type', 'mcp_internal')}
                                  >
                                    <CardIcon>
                                      <FontAwesomeIcon className="svg-inline--fa fa-network-wired" icon={faNetworkWired} color="#0066FF" />
                                    </CardIcon>
                                    <CardTitle selected={(tool.type || 'mcp_internal') === 'mcp_internal'}>Internal Pipeline Tool</CardTitle>
                                    <CardDescription>Execute an internal pipeline tool.</CardDescription>
                                  </SelectableCard>
                                  <SelectableCard
                                    role="button"
                                    aria-pressed={tool.type === 'mcp_external'}
                                    selected={tool.type === 'mcp_external'}
                                    onClick={() =>
                                      handleUpdateToolSetting(
                                        index,
                                        'type',
                                        'mcp_external'
                                      )
                                    }
                                  >
                                    <CardIcon>
                                      <FontAwesomeIcon
                                        icon={faNetworkWired}
                                        color="#0066FF"
                                      />
                                    </CardIcon>

                                    <CardTitle
                                      selected={tool.type === 'mcp_external'}
                                    >
                                      External MCP Tool
                                    </CardTitle>

                                    <CardDescription>
                                      Use tools exposed by a configured external MCP server.
                                    </CardDescription>
                                  </SelectableCard>
                                  <SelectableCard
                                    role="button"
                                    aria-pressed={tool.type === 'entity'}
                                    selected={tool.type === 'entity'}
                                    onClick={() => handleUpdateToolSetting(index, 'type', 'entity')}
                                  >
                                    <CardIcon>
                                      <FontAwesomeIcon className="svg-inline--fa fa-window-restore" icon={faWindowRestore} color="#0066FF" />
                                    </CardIcon>
                                    <CardTitle selected={tool.type === 'entity'}>Entity Tool</CardTitle>
                                    <CardDescription>Fetch data about entities from a configured source.</CardDescription>
                                  </SelectableCard>
                                  <SelectableCard
                                    role="button"
                                    aria-pressed={
                                      tool.type === INTERNAL_PIPELINE_TOOL_TYPE
                                    }
                                    selected={
                                      tool.type === INTERNAL_PIPELINE_TOOL_TYPE
                                    }
                                    onClick={() =>
                                      handleUpdateToolSetting(
                                        index,
                                        'type',
                                        INTERNAL_PIPELINE_TOOL_TYPE
                                      )
                                    }
                                  >
                                    <CardIcon>
                                      <FontAwesomeIcon
                                        className="svg-inline--fa fa-network-wired"
                                        icon={faNetworkWired}
                                        color="#0066FF"
                                      />
                                    </CardIcon>

                                    <CardTitle
                                      selected={
                                        tool.type === INTERNAL_PIPELINE_TOOL_TYPE
                                      }
                                    >
                                      Pipeline Tool
                                    </CardTitle>

                                    <CardDescription>
                                      Execute an internal pipeline directly.
                                    </CardDescription>
                                  </SelectableCard>

                                </CardGrid>
                              </div>

                              {(() => {
                                const ENTITY_OPERATION_HELP = { fetch: 'Read existing records (no create/update/delete)' };
                                return (
                                  <>
                                    {(tool.type || 'mcp_internal') === 'mcp_internal' && (
                                      <div>
                                        <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                                          Internal Tool
                                        </Label>
                                        {hasToolPipelines ? (
                                          <MultiSelectDropdownSearch
                                            options={normalizedToolPipelines}
                                            selected={
                                              tool.code
                                                ? normalizedToolPipelines.filter((p) => p.code === tool.code)
                                                : []
                                            }
                                            onChange={(newSelection) => {
                                              const last = newSelection[newSelection.length - 1];
                                              const newCode = last ? last.code : '';
                                              handleUpdateToolSetting(index, 'code', newCode);
                                            }}
                                            placeholder="Select Internal Tool"
                                            searchPlaceholder="Search Internal Tools..."
                                            isSingleSelect
                                          />
                                        ) : (
                                          <Select disabled>
                                            <option value="">No Internal Tools available</option>
                                          </Select>
                                        )}
                                      </div>
                                    )}

                                    {tool.type === 'entity' && (
                                      <div>
                                        <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Entity</Label>
                                        {entityOptionsLoading ? (
                                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading entities...</div>
                                        ) : (
                                          <Select
                                            value={tool.entity_code || ''}
                                            onChange={(e) => handleUpdateToolSetting(index, 'entity_code', e.target.value)}
                                          >
                                            <option value="">Select Entity</option>
                                            {entityOptions.map((opt) => (
                                              <option key={opt.code} value={opt.code}>{opt.name}</option>
                                            ))}
                                          </Select>
                                        )}

                                        <div style={{ marginTop: '0.75rem' }}>
                                          <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Operations</Label>
                                          <MultiSelectDropdownSearch
                                            options={[{ name: 'fetch', code: 'fetch' }]}
                                            selected={(tool.operations || ['fetch']).map(op => ({ name: op, code: op }))}
                                            onChange={(newSelection) => {
                                              const ops = newSelection.map(x => x.code || x.name);
                                              handleUpdateToolSetting(index, 'operations', ops);
                                            }}
                                            placeholder="Select operations"
                                          />
                                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                                            {(tool.operations || ['fetch']).map(op => (
                                              <div key={op}><strong style={{ textTransform: 'lowercase' }}>{op}</strong>: {ENTITY_OPERATION_HELP[op] || ''}</div>
                                            ))}
                                          </div>
                                        </div>

                                        <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                          <div>
                                            <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Read Only</Label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <ToggleSwitch
                                                role="switch"
                                                aria-checked={tool.read_only !== false}
                                                on={tool.read_only !== false}
                                                onClick={() => handleUpdateToolSetting(index, 'read_only', !(tool.read_only !== false))}
                                              />
                                            </div>
                                          </div>
                                          <div>
                                            <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Default Limit</Label>
                                            <Input
                                              type="number"
                                              min={1}
                                              value={typeof tool.default_limit === 'number' ? tool.default_limit : 200}
                                              onChange={(e) => handleUpdateToolSetting(index, 'default_limit', Number(e.target.value) || 1)}
                                            />
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                                              Maximum number of records to return per request.
                                            </div>
                                          </div>
                                        </div>

                                        <input type="hidden" value={tool.sub_type || 'entity'} readOnly />
                                        <input type="hidden" value={tool.code || 'entity_data_tool'} readOnly />
                                        <div style={{ marginTop: '0.75rem' }}>
                                          <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Instructions</Label>
                                          <Textarea
                                            placeholder="Optional guidance for how the agent should use this tool"
                                            value={tool.instructions || ''}
                                            onChange={(e) => handleUpdateToolSetting(index, 'instructions', e.target.value)}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {tool.type === INTERNAL_PIPELINE_TOOL_TYPE && (
                                    <div>
                                      <Label
                                        style={{
                                          fontSize: '0.8rem',
                                          marginBottom: '0.25rem',
                                        }}
                                      >
                                        Pipeline
                                      </Label>

                                      {hasToolPipelines ? (
                                        <MultiSelectDropdownSearch
                                          options={normalizedToolPipelines}
                                          selected={
                                            tool.code
                                              ? normalizedToolPipelines.filter(
                                                  (pipeline) =>
                                                    pipeline.code === tool.code
                                                )
                                              : []
                                          }
                                          onChange={(newSelection) => {
                                            const last =
                                              newSelection[newSelection.length - 1];

                                            const newCode = last ? last.code : '';

                                            handleUpdateToolSetting(
                                              index,
                                              'code',
                                              newCode
                                            );
                                          }}
                                          placeholder="Select Pipeline"
                                          searchPlaceholder="Search Pipelines..."
                                          isSingleSelect
                                        />
                                      ) : (
                                        <Select disabled>
                                          <option value="">
                                            No Pipelines available
                                          </option>
                                        </Select>
                                      )}

                                      <div style={{ marginTop: '0.75rem' }}>
                                        <Label
                                          style={{
                                            fontSize: '0.8rem',
                                            marginBottom: '0.25rem',
                                          }}
                                        >
                                          Instructions
                                        </Label>

                                        <Textarea
                                          placeholder="Optional guidance for how the planner should use this pipeline"
                                          value={tool.instructions || ''}
                                          onChange={(e) =>
                                            handleUpdateToolSetting(
                                              index,
                                              'instructions',
                                              e.target.value
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {tool.type === 'mcp_external' && (
                                  <div>
                                    <Label
                                      style={{
                                        fontSize: '0.8rem',
                                        marginBottom: '0.25rem',
                                      }}
                                    >
                                      External MCP Tool
                                    </Label>

                                    {externalMcpToolsLoading ? (
                                      <Select disabled>
                                        <option>
                                          Loading External MCP Tools...
                                        </option>
                                      </Select>
                                    ) : normalizedExternalMcpTools.length > 0 ? (
                                      <MultiSelectDropdownSearch
                                        options={normalizedExternalMcpTools.filter(
                                          (mcpTool) =>
                                            mcpTool.code === tool.code ||
                                            !isExternalMcpAlreadySelected(
                                              mcpTool.code,
                                              index
                                            )
                                        )}
                                        selected={
                                          tool.code
                                            ? normalizedExternalMcpTools.filter(
                                                (mcpTool) =>
                                                  mcpTool.code === tool.code
                                              )
                                            : []
                                        }
                                        onChange={(newSelection) => {
                                          const selectedTool =
                                            newSelection[newSelection.length - 1];

                                          handleUpdateToolSetting(
                                            index,
                                            'code',
                                            selectedTool?.code || ''
                                          );
                                        }}
                                        placeholder="Select External MCP Tool"
                                        searchPlaceholder="Search External MCP Tools..."
                                        isSingleSelect
                                      />
                                    ) : (
                                      <Select disabled>
                                        <option value="">
                                          No External MCP Tools available
                                        </option>
                                      </Select>
                                    )}

                                    <div
                                      style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--text-secondary)',
                                        marginTop: '0.35rem',
                                      }}
                                    >
                                      Select an External MCP tool configured in the Tools screen.
                                    </div>

                                    <div style={{ marginTop: '0.75rem' }}>
                                      <Label
                                        style={{
                                          fontSize: '0.8rem',
                                          marginBottom: '0.25rem',
                                        }}
                                      >
                                        Instructions
                                      </Label>

                                      <Textarea
                                        value={tool.instructions || ''}
                                        placeholder="Optional planner-specific instructions for using this MCP tool"
                                        onChange={(event) =>
                                          handleUpdateToolSetting(
                                            index,
                                            'instructions',
                                            event.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                )}


                                  </>
                                );
                              })()}

                              <div style={{ display: 'flex', alignItems: 'center', height: '100%', marginTop: '0.75rem' }}>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveToolSetting(index)}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(239,68,68,0.4)',
                                    background: 'rgba(239,68,68,0.04)',
                                    color: '#b91c1c',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                  }}
                                  aria-label="Remove tool"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={handleAddToolSetting}
                          style={{
                            padding: '0.6rem 1rem',
                            borderRadius: '6px',
                            border: '1px dashed rgba(0, 102, 255, 0.5)',
                            background: 'rgba(0, 102, 255, 0.02)',
                            color: 'var(--primary)',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                          }}
                        >
                          + Add Tool
                        </button>
                      </FormGroup>
                    </>
                  );
                })()}
              </>
            )}
          </>
        )}

        {currentSetupStep === 3 && (
          <>
            <InfoBox>
              Configure the prompts that define your planner's behavior. Well-crafted prompts are essential for optimal performance.
            </InfoBox>

            <TabsContainer>
              <ModalTab
                active={modalTabPrompt === 'system'}
                onClick={() => setModalTabPrompt('system')}
              >
                System Prompt
              </ModalTab>
              <ModalTab
                active={modalTabPrompt === 'examples'}
                onClick={() => setModalTabPrompt('examples')}
              >
                Example Conversations
              </ModalTab>
              <ModalTab
                active={modalTabPrompt === 'templates'}
                onClick={() => setModalTabPrompt('templates')}
              >
                Response Templates
              </ModalTab>
            </TabsContainer>

            {modalTabPrompt === 'system' && (
              <>
                <FormGroup>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Label>System Prompt</Label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        onClick={() => setShowPromptTemplates(!showPromptTemplates)}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        Load Template
                      </Button>
                      <Button
                        onClick={() => {
                          setSystemPromptDraft('');
                          handleNestedFieldChange('prompt', 'system_prompt', '');
                        }}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      marginTop: '0.75rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setSystemPromptMode('editor')}
                      style={{
                        padding: '0.4rem 0.9rem',
                        fontSize: '0.8rem',
                        borderRadius: '999px',
                        border:
                          systemPromptMode === 'editor'
                            ? '1px solid var(--primary)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        background:
                          systemPromptMode === 'editor'
                            ? 'var(--primary)'
                            : '#ffffff',
                        color: systemPromptMode === 'editor' ? '#ffffff' : 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      Editor
                    </button>
                    <button
                      type="button"
                      onClick={() => setSystemPromptMode('preview')}
                      style={{
                        padding: '0.4rem 0.9rem',
                        fontSize: '0.8rem',
                        borderRadius: '999px',
                        border:
                          systemPromptMode === 'preview'
                            ? '1px solid var(--primary)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        background:
                          systemPromptMode === 'preview'
                            ? 'var(--primary)'
                            : '#ffffff',
                        color: systemPromptMode === 'preview' ? '#ffffff' : 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      Preview
                    </button>
                  </div>

                  {showPromptTemplates ? (
                    <div
                      style={{
                        background: 'rgba(0, 102, 255, 0.05)',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        fontSize: '0.9rem',
                      }}
                    >
                      <div style={{ fontWeight: '500', marginBottom: '0.75rem' }}>
                        Sample system prompts
                      </div>
                      <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
                        <li
                          style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
                          onClick={() => {
                            const value =
                              'You are a planner that orchestrates tasks across insurance workflows.';
                            setSystemPromptDraft(value);
                            handleNestedFieldChange('prompt', 'system_prompt', value);
                            setShowPromptTemplates(false);
                          }}
                        >
                          Insurance workflow orchestration
                        </li>
                        <li
                          style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
                          onClick={() => {
                            const value =
                              'You are a planner focused on claims processing, routing complex requests to the right agents.';
                            setSystemPromptDraft(value);
                            handleNestedFieldChange('prompt', 'system_prompt', value);
                            setShowPromptTemplates(false);
                          }}
                        >
                          Claims processing planner
                        </li>
                      </ul>
                    </div>
                  ) : (
                    <>
                      {systemPromptMode === 'editor' ? (
                        <Textarea
                          placeholder="Describe in detail how this planner should behave, its goals, and constraints."
                          value={systemPromptDraft}
                          onChange={(e) => setSystemPromptDraft(e.target.value)}
                          onBlur={() =>
                            handleNestedFieldChange(
                              'prompt',
                              'system_prompt',
                              systemPromptDraft
                            )
                          }
                          style={{ minHeight: '200px', fontFamily: 'monospace' }}
                        />
                      ) : 
                      (
                                              // <div style={{
                                              //   border: '1px solid rgba(0, 102, 255, 0.1)',
                                              //   borderRadius: '8px',
                                              //   padding: '1rem',
                                              //   minHeight: '200px',
                                              //   background: '#ffffff'
                                              // }}>
                                              //   {/* Render markdown to sanitized HTML to avoid parser issues for very large prompts */}
                                              //   <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(systemPromptDraft || '')) }} />
                                              // </div>

                                              <PromptPreview systemPromptDraft={systemPromptDraft} />
                                            )
                      }
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem',
                        }}
                      >
                        <span>Tokens: ~{Math.round(systemPromptDraft.length / 4)}</span>
                        <span>{systemPromptDraft.length} characters</span>
                      </div>
                    </>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Variables</Label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0.5rem',
                      marginBottom: '1rem',
                    }}
                  >
                    {[
                      '{{user_name}}',
                      '{{company_name}}',
                      '{{current_date}}',
                      '{{policy_number}}',
                      '{{planner_name}}',
                    ].map((variable) => (
                      <div
                        key={variable}
                        onClick={() => {
                          const textarea = document.querySelector('textarea');
                          if (!textarea) return;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const newValue =
                            systemPromptDraft.substring(0, start) +
                            variable +
                            systemPromptDraft.substring(end);
                          setSystemPromptDraft(newValue);
                        }}
                        style={{
                          padding: '0.5rem',
                          background: 'rgba(0, 102, 255, 0.1)',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          textAlign: 'center',
                        }}
                      >
                        {variable}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Click on a variable to insert it at the cursor position in the system
                    prompt
                  </div>
                </FormGroup>
              </>
            )}

            {modalTabPrompt === 'examples' && (
              <>
                <FormGroup>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <Label style={{ margin: 0 }}>Example Conversations</Label>
                    <Button
                      onClick={() => {
                        setEditingExampleIndex(-1);
                        setCurrentExample({
                          title: '',
                          conversations: [],
                        });
                        setShowExampleEditor(true);
                      }}
                    >
                      Add Example
                    </Button>
                  </div>

                  {exampleConversations.length === 0 ? (
                    <div
                      style={{
                        padding: '2rem',
                        textAlign: 'center',
                        background: 'rgba(0, 102, 255, 0.02)',
                        borderRadius: '8px',
                        border: '1px dashed rgba(0, 102, 255, 0.2)',
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📝</div>
                      <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                        No examples yet
                      </div>
                      <div
                        style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}
                      >
                        Add example conversations to help your planner understand expected
                        interactions
                      </div>
                    </div>
                  ) : (
                    <div>
                      {exampleConversations.map((example, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: '1.5rem',
                            background: 'rgba(0, 102, 255, 0.02)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 102, 255, 0.1)',
                            padding: '1rem',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '1rem',
                            }}
                          >
                            <div style={{ fontWeight: '500' }}>{example.title}</div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                onClick={() => {
                                  setEditingExampleIndex(index);
                                  setCurrentExample(example);
                                  setShowExampleEditor(true);
                                }}
                                style={{ padding: '0.5rem', fontSize: '0.8rem' }}
                              >
                                Edit
                              </Button>
                              <Button
                                onClick={() => {
                                  const updated = exampleConversations.filter(
                                    (_, i) => i !== index
                                  );
                                  setExampleConversations(updated);
                                  setSelectedPlanner((prev) => ({
                                    ...prev,
                                    prompt: {
                                      ...(prev.prompt || {}),
                                      example_conversation: updated,
                                    },
                                  }));
                                }}
                                style={{
                                  padding: '0.5rem',
                                  fontSize: '0.8rem',
                                  background: 'var(--danger)',
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>

                          {(example.conversations || []).map((message, msgIndex) => (
                            <div
                              key={msgIndex}
                              style={{
                                padding: '0.75rem',
                                background:
                                  message.role === 'user'
                                    ? 'rgba(0, 102, 255, 0.05)'
                                    : 'white',
                                borderRadius: '8px',
                                marginBottom: '0.5rem',
                                border:
                                  message.role === 'user'
                                    ? 'none'
                                    : '1px solid rgba(0, 102, 255, 0.1)',
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: '500',
                                  marginBottom: '0.5rem',
                                  color:
                                    message.role === 'user'
                                      ? 'var(--primary)'
                                      : 'var(--text-primary)',
                                }}
                              >
                                {message.role === 'user' ? 'User' : 'Planner'}:
                              </div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {message.content}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </FormGroup>

                {showExampleEditor && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        width: '80%',
                        maxWidth: '800px',
                        maxHeight: '80vh',
                        overflow: 'auto',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '1.5rem',
                        }}
                      >
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                          {editingExampleIndex === -1
                            ? 'Add Example Conversation'
                            : 'Edit Example Conversation'}
                        </h3>
                        <Button
                          onClick={() => setShowExampleEditor(false)}
                          style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                        >
                          Close
                        </Button>
                      </div>

                      <FormGroup>
                        <Label>Title</Label>
                        <Input
                          type="text"
                          value={currentExample.title}
                          onChange={(e) =>
                            setCurrentExample((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>

                      <div style={{ marginBottom: '1.5rem' }}>
                        {(currentExample.conversations || []).map((message, index) => (
                          <div
                            key={index}
                            style={{
                              marginBottom: '1rem',
                              background: 'rgba(0, 102, 255, 0.02)',
                              borderRadius: '8px',
                              padding: '1rem',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.5rem',
                              }}
                            >
                              <Select
                                value={message.role}
                                onChange={(e) => {
                                  const updated = [...currentExample.conversations];
                                  updated[index] = {
                                    ...updated[index],
                                    role: e.target.value,
                                  };
                                  setCurrentExample((prev) => ({
                                    ...prev,
                                    conversations: updated,
                                  }));
                                }}
                                style={{ width: '150px' }}
                              >
                                <option value="user">User</option>
                                <option value="assistant">Assistant</option>
                                <option value="system">System</option>
                              </Select>
                              <Button
                                onClick={() => {
                                  const updated = currentExample.conversations.filter(
                                    (_, i) => i !== index
                                  );
                                  setCurrentExample((prev) => ({
                                    ...prev,
                                    conversations: updated,
                                  }));
                                }}
                                style={{
                                  padding: '0.5rem',
                                  fontSize: '0.8rem',
                                  background: 'var(--danger)',
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                            <Textarea
                              value={message.content}
                              onChange={(e) => {
                                const updated = [...currentExample.conversations];
                                updated[index] = {
                                  ...updated[index],
                                  content: e.target.value,
                                };
                                setCurrentExample((prev) => ({
                                  ...prev,
                                  conversations: updated,
                                }));
                              }}
                              placeholder={`Enter ${message.role} message`}
                              style={{ minHeight: '100px' }}
                            />
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button
                          onClick={() => {
                            setCurrentExample((prev) => ({
                              ...prev,
                              conversations: [
                                ...((prev.conversations || [])),
                                { role: 'user', content: '' },
                              ],
                            }));
                          }}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                        >
                          Add Message
                        </Button>
                        <Button
                          primary
                          onClick={() => {
                            const updated = [...exampleConversations];
                            if (editingExampleIndex === -1) {
                              updated.push(currentExample);
                            } else {
                              updated[editingExampleIndex] = currentExample;
                            }
                            setExampleConversations(updated);
                            setSelectedPlanner((prev) => ({
                              ...prev,
                              prompt: {
                                ...(prev.prompt || {}),
                                example_conversation: updated,
                              },
                            }));
                            setShowExampleEditor(false);
                            setEditingExampleIndex(-1);
                            setCurrentExample({ title: '', conversations: [] });
                          }}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                        >
                          Save Conversation
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {modalTabPrompt === 'templates' && (
              <>
                <FormGroup>
                  <Label>Response Templates</Label>
                  <div
                    style={{
                      background: 'rgba(0, 102, 255, 0.02)',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 102, 255, 0.1)',
                      padding: '1.5rem',
                    }}
                  >
                    <p
                      style={{
                        fontSize: '0.9rem',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Configure how planner responses should be structured (for now this is
                      informational only).
                    </p>
                    <Textarea
                      placeholder="Describe the ideal response format for this planner (e.g. JSON schema, bullet list, etc.)"
                      value={
                        selectedPlanner.prompt?.response_template?.template_content || ''
                      }
                      onChange={(e) =>
                        setSelectedPlanner((prev) => ({
                          ...prev,
                          prompt: {
                            ...(prev.prompt || {}),
                            response_template: {
                              ...(prev.prompt?.response_template || {}),
                              template_content: e.target.value,
                            },
                          },
                        }))
                      }
                      style={{ minHeight: '150px' }}
                    />
                  </div>
                </FormGroup>
              </>
            )}
          </>
        )}

        {currentSetupStep === 4 && (
          <>
            <InfoBox>
              Configure basic security and guardrails for this planner.
            </InfoBox>

            <TabsContainer>
              <ModalTab
                active={modalTabSecurity === 'security'}
                onClick={() => setModalTabSecurity('security')}
              >
                Security Settings
              </ModalTab>
              <ModalTab
                active={modalTabSecurity === 'guardrails'}
                onClick={() => setModalTabSecurity('guardrails')}
              >
                Content Guardrails
              </ModalTab>
              <ModalTab
                active={modalTabSecurity === 'access'}
                onClick={() => setModalTabSecurity('access')}
              >
                Access Control
              </ModalTab>
            </TabsContainer>

            {modalTabSecurity === 'security' && (
              <>
                <FormGroup>
                  <Label>
                    <input
                      type="checkbox"
                      style={{ marginRight: '0.5rem' }}
                      checked={selectedPlanner.security?.security_settings?.data_encryption || false}
                      onChange={(e) =>
                        setSelectedPlanner((prev) => ({
                          ...prev,
                          security: {
                            ...(prev.security || {}),
                            security_settings: {
                              ...(prev.security?.security_settings || {}),
                              data_encryption: e.target.checked,
                            },
                          },
                        }))
                      }
                    />
                    Enable data encryption
                  </Label>
                </FormGroup>
                <FormGroup>
                  <Label>
                    <input
                      type="checkbox"
                      style={{ marginRight: '0.5rem' }}
                      checked={selectedPlanner.security?.security_settings?.pii_handling || false}
                      onChange={(e) =>
                        setSelectedPlanner((prev) => ({
                          ...prev,
                          security: {
                            ...(prev.security || {}),
                            security_settings: {
                              ...(prev.security?.security_settings || {}),
                              pii_handling: e.target.checked,
                            },
                          },
                        }))
                      }
                    />
                    Enable PII handling
                  </Label>
                </FormGroup>
              </>
            )}

            {modalTabSecurity === 'guardrails' && (
              <>
                <FormGroup>
                  <GuardrailsEditor
                    aiGuardrails={selectedPlanner?.security?.ai_guardrails || []}
                    onChange={(list) => updateSelectedPlanner('security.ai_guardrails', list)}
                  />
                </FormGroup>
              </>
            )}

            {modalTabSecurity === 'access' && (
              <>
              </>
            )}
          </>
        )}

        {currentSetupStep === 5 && (
          <>
            <InfoBox>
              Review your planner configuration before finalizing. Ensure all settings are
              correct and meet your requirements.
            </InfoBox>
            <div
              style={{
                background: 'rgba(0, 102, 255, 0.02)',
                borderRadius: '8px',
                border: '1px solid rgba(0, 102, 255, 0.1)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  marginBottom: '1.5rem',
                }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '16px',
                    background: 'var(--gradient-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem',
                    color: '#ffffff',
                  }}
                >
                  <img
                    src={getSafeIcon(selectedPlanner?.icon)}
                    alt="Planner Icon"
                    style={{ height: '50px', width: '50px' }}
                  />
                </div>
                <div>
                  <h2
                    style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}
                  >
                    {selectedPlanner.name || 'Unnamed Planner'}
                  </h2>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    {selectedPlanner.type} •{' '}
                    {(selectedPlanner.business_domain || '')
                      .replace(/_/g, ' ')
                      .trim()}
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontSize: '0.9rem',
                  marginBottom: '1.5rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selectedPlanner.description}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1.5rem',
                  fontSize: '0.9rem',
                }}
              >
                <div>
                  <h3 style={{ fontSize: '1rem', marginTop: 0 }}>
                    Model & Knowledge
                  </h3>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>AI Model:</strong>{' '}
                    {modelList.find(
                      (model) =>
                        model.code ===
                        selectedPlanner.general_settings?.model_code
                    )?.name || 'Unknown Model'}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Temperature:</strong>{' '}
                    {selectedPlanner.general_settings?.model_temperature}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Max Tokens:</strong>{' '}
                    {selectedPlanner.general_settings?.max_tokens}
                  </div>
                  <div>
                    <strong>Knowledge Base:</strong>{' '}
                    {kbList.find(
                      (kb) =>
                        kb.code ===
                        selectedPlanner.knowledge_base?.knowledgebase_code
                    )?.name || 'None'}
                  </div>
                </div>

                <div>
                  <h3 style={{ fontSize: '1rem', marginTop: 0 }}>
                    Security & Access
                  </h3>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Data Encryption:</strong>{' '}
                    {selectedPlanner.security?.security_settings?.data_encryption
                      ? 'Enabled'
                      : 'Disabled'}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>PII Handling:</strong>{' '}
                    {selectedPlanner.security?.security_settings?.pii_handling
                      ? 'Enabled'
                      : 'Disabled'}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>MCP Servers:</strong>{' '}
                    {selectedPlanner.mcp_servers &&
                    Object.keys(selectedPlanner.mcp_servers).length > 0
                      ? Object.keys(selectedPlanner.mcp_servers).length +
                        ' configured'
                      : 'None'}
                  </div>
                </div>
              </div>
            </div>

            <FormGroup>
              <Label>Validation Status</Label>
              <div
                style={{
                  padding: '1rem',
                  background:
                    validationErrors.length > 0
                      ? 'rgba(255, 0, 0, 0.05)'
                      : 'rgba(0, 200, 0, 0.05)',
                  borderRadius: '8px',
                  border: `1px solid ${
                    validationErrors.length > 0
                      ? 'rgba(255, 0, 0, 0.2)'
                      : 'rgba(0, 200, 0, 0.2)'
                  }`,
                  marginBottom: '1.5rem',
                }}
              >
                {validationErrors.length > 0 ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: 'var(--danger)',
                        fontWeight: '500',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                      <span>
                        Please fix the following issues before saving your planner:
                      </span>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                      {validationErrors.map((error, index) => (
                        <li
                          key={index}
                          style={{ color: 'var(--danger)' }}
                        >
                          {error}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: 'var(--success)',
                      fontWeight: '500',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>✅</span>
                    <span>
                      All required fields are complete. Your planner is ready to be
                      created!
                    </span>
                  </div>
                )}
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Deployment Options</Label>
              <CardGrid style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                {[
                  {
                    id: 'development',
                    name: 'Development',
                    icon: '🧪',
                    description: 'Test environment with debugging features',
                  },
                  {
                    id: 'staging',
                    name: 'Staging',
                    icon: '🔍',
                    description: 'Pre-production environment for testing',
                  },
                  {
                    id: 'production',
                    name: 'Production',
                    icon: '🚀',
                    description: 'Live environment for end users',
                  },
                ].map((env) => (
                  <SelectableCard
                    key={env.id}
                    selected={deploymentEnvironment === env.id}
                    onClick={() => setDeploymentEnvironment(env.id)}
                  >
                    <CardIcon selected={deploymentEnvironment === env.id}>
                      {env.icon}
                    </CardIcon>
                    <CardTitle selected={deploymentEnvironment === env.id}>
                      {env.name}
                    </CardTitle>
                    <CardDescription>{env.description}</CardDescription>
                  </SelectableCard>
                ))}
              </CardGrid>
            </FormGroup>

            <FormGroup>
              <Label>Deployment Schedule</Label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <Select
                  value={deploymentType}
                  onChange={(e) => setDeploymentType(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="immediate">Deploy Immediately</option>
                  <option value="scheduled">Schedule Deployment</option>
                </Select>

                {deploymentType === 'scheduled' && (
                  <Input
                    type="datetime-local"
                    value={deploymentSchedule}
                    onChange={(e) => setDeploymentSchedule(e.target.value)}
                    style={{ flex: 1 }}
                  />
                )}
              </div>

              <div
                style={{
                  padding: '1rem',
                  background: 'rgba(0, 102, 255, 0.05)',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                }}
              >
                <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                  Deployment Notes:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li>
                    Development deployments are available immediately for testing
                  </li>
                  <li>
                    Staging and Production deployments may require additional approvals
                  </li>
                  <li>
                    You can modify your planner settings after deployment
                  </li>
                </ul>
              </div>
            </FormGroup>
          </>
        )}

        <ModalFooter>
          <div>
            {currentSetupStep > 1 && (
              <Button onClick={prevStep}>Back</Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {currentSetupStep < 5 && (
              <Button primary onClick={nextStep}>Next</Button>
            )}
            {currentSetupStep === 5 && (
              <Button primary onClick={handleSubmitPlanner}>
                {selectedPlanner.id ? 'Save Changes' : 'Create Planner'}
              </Button>
            )}
          </div>
        </ModalFooter>
      </ModalContent>
    );
  };

  console.log("filterd list :", filteredPlannerList)

  return (
    <Container>
      <PageHeaderWithInfo
        icon={faChartDiagram}
        title="Planner Studio"
        description="Design, manage, and monitor your planners from a single dashboard"
      >
        <div style={{ display: 'flex', gap: '1rem' }}>
          <SearchBar>
            <SearchInput
              placeholder="Search planners..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
          <Button
            primary
            onClick={() => openPlannerModal()}
          >
            <span style={{ marginRight: '0.5rem' }}>+</span> Design Planner
          </Button>
        </div>
      </PageHeaderWithInfo>

      <SectionTitle>Active Planners <span>{totalItems}</span></SectionTitle>

      {isLoading ? (
        <div style={{ padding: "2rem", display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                 <LoaderContainer>
                  </LoaderContainer>
                  <span>Loading...</span>
                </div>
      ) : filteredPlannerList.length > 0 ? ( 
        <Grid>
          {filteredPlannerList.map(planner => (
            <PlannerCard key={planner.id} whileHover={{ y: -5 }}>
              <PlannerHeader>
                <PlannerIcon>
                  <img
                    src={getSafeIcon(planner.icon)}
                    alt="Planner Icon"
                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                  />
                </PlannerIcon>
                <PlannerInfo>
                  <PlannerName>{planner.name}</PlannerName>
                  <PlannerType>{planner.type}</PlannerType>
                </PlannerInfo>
              </PlannerHeader>
              <PlannerStatus active={planner.status === 'ACTIVE'}>
                {planner.status === 'ACTIVE' ? 'Active' : 'Inactive'}
              </PlannerStatus>
              <PlannerDescription>
                {planner.description || 'No description provided.'}
              </PlannerDescription>
              <ButtonGroup>
                <PlannerButton primary onClick={() => openPlannerModal(planner)}>Edit</PlannerButton>
                <PlannerButton onClick={() => toggleStatusOfThePlanner(planner)}>{planner?.status == "ACTIVE" ? 'Deactivate' : 'Activate'}</PlannerButton>
                <PlannerButton disabled={planner?.status !== "ACTIVE"} onClick={() => handleRunPlanner(planner.code)}>Run</PlannerButton>
              </ButtonGroup>
            </PlannerCard>
          ))}
        </Grid>
      ) : (
          (<EmptyStatePage
                    title="No Planners found"
                    subtitle="We couldn't find any planners matching your search criteria. Try adjusting your search terms or design a new planner."
                    icon="data"
                    primaryAction={{
                      label: 'Go Back',
                      onClick: () => {}
                    }}
                    secondaryAction={{
                      label: 'Go Home',
                      // onClick: openTemplates
                    }}
                  />)
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalItems}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
      />

      {openPlannerCopilot && (
        <SideChatPanel
          width={chatPanelWidth}
          style={{ right: 0, left: 'auto', top: 0, bottom: 0 }}
        >
          <ResizeHandle
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat panel"
            onMouseDown={(e) => onResizeStart(e.clientX)}
            onTouchStart={(e) => onResizeStart(e.touches[0].clientX)}
            onDoubleClick={() => setChatPanelWidth(420)}
            style={{ left: -8, right: 'auto' }}
          />
          <CloseChatButton
            aria-label="Close chat"
            onClick={() => setOpenPlannerCopilot(false)}
          >
            ✕
          </CloseChatButton>
          <CoPilotChat
            messages={[]}
            messageLoader={false}
            sessionId={`${uuid()}`}
            initialUserMessage={'Hi'}
            containerStyle={{ height: '100%' }}
            streamUrl={`/api/v1/mms_ai_executors/planner/execute/sync/stream`}
            plannerCd={currentPlannerToRun}
          />
        </SideChatPanel>
      )}

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closePlannerModal}
        >
          <div onClick={(e) => e.stopPropagation()}>
            {renderPlannerSetupModal()}
          </div>
        </Modal>
      )}
    </Container>
  );
};

export default Planner;
